import { useEffect, useRef } from "react";
import { type FieldPath, type UseFormReturn } from "react-hook-form";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";

/**
 * Type guard to check if an element is an input or textarea
 */
const isInputOrTextarea = (
  el: EventTarget | null
): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

/**
 * Hook to track the currently focused form field
 *
 * This is useful when you need to preserve the value of a focused field
 * during form resets or other operations that might otherwise lose user input.
 */
export const useFocusTracking = (
  methods: UseFormReturn<OrderItemConfigurationSchema>
) => {
  const focusedFieldRef = useRef<{
    path: FieldPath<OrderItemConfigurationSchema>;
    value: unknown;
  } | null>(null);

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if (!isInputOrTextarea(e.target) || !e.target.name) {
        return;
      }

      const fieldName = e.target
        .name as FieldPath<OrderItemConfigurationSchema>;
      focusedFieldRef.current = {
        path: fieldName,
        value: methods.getValues(fieldName),
      };
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (!isInputOrTextarea(e.target) || !e.target.name) {
        return;
      }

      if (
        focusedFieldRef.current &&
        e.target.name === focusedFieldRef.current.path
      ) {
        focusedFieldRef.current = null;
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [methods]);

  return focusedFieldRef;
};
